import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Briefcase, Archive, Pencil, RotateCcw, Search } from 'lucide-react';
import type { JobRole } from '@hcc/types';
import { ApiError } from '../../lib/api';
import { useAllRoles, useArchiveRole, useRestoreRole } from '../../hooks/useRoles';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardSection } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Pill } from '../../components/ui/Pill';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonList } from '../../components/ui/Skeleton';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { RolesImportCard } from './RolesImportCard';
import { RoleCategoriesCard } from './RoleCategoriesCard';
import { SapRoleSyncCard } from './SapRoleSyncCard';
import { PendingSapRolesCard } from './PendingSapRolesCard';
import { DuplicateSapJobsCard } from './DuplicateSapJobsCard';
import { RoleForm } from './RoleForm';
import { useRoleCategories } from '../../hooks/useRoleCategories';

type TagFilter = 'conflict' | 'no_tariff' | 'proj';

const FILTERS: { key: TagFilter; label: string }[] = [
  { key: 'conflict', label: 'Conflict' },
  { key: 'no_tariff', label: 'No tariff' },
  { key: 'proj', label: 'PROJ' },
];

function isSapLinked(r: JobRole): boolean {
  return !!(r.external_ref && r.sap_synced_at);
}

function hasProjVariant(r: JobRole): boolean {
  return r.sap_resources.some((res) => res.id.endsWith('_PROJ'));
}

function matchesFilter(r: JobRole, f: TagFilter): boolean {
  if (f === 'conflict') return r.sap_conflicts.length > 0;
  if (f === 'no_tariff') return isSapLinked(r) && !r.sap_missing && r.sap_resources.length === 0;
  return hasProjVariant(r);
}

function matchesSearch(r: JobRole, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return (
    r.name.toLowerCase().includes(needle) ||
    (r.external_ref ?? '').toLowerCase().includes(needle) ||
    r.sap_resources.some((res) => res.id.toLowerCase().includes(needle))
  );
}

export function RolesAdmin() {
  const list = useAllRoles();
  const categories = useRoleCategories();
  const archive = useArchiveRole();
  const restore = useRestoreRole();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [confirm, setConfirm] = useState<{ id: string; name: string } | null>(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<TagFilter[]>([]);

  const roles = list.data ?? [];
  const active = roles.filter((r) => !r.archived_at);
  const archived = roles.filter((r) => r.archived_at);
  // Unlinked active roles are by definition awaiting creation in SAP.
  const pending = active.filter((r) => !isSapLinked(r));
  const linked = active.filter(isSapLinked);
  const categoryList = categories.data ?? [];

  const visible = useMemo(
    () =>
      linked.filter(
        (r) => matchesSearch(r, search) && filters.every((f) => matchesFilter(r, f)),
      ),
    [linked, search, filters],
  );

  return (
    <main className="mx-auto max-w-3xl space-y-5 px-4 py-8">
      <PageHeader
        eyebrowIcon={Briefcase}
        eyebrow="People"
        title="Job roles"
        description="The master list of roles people can hold, synced from SAP. New roles are born in SAP, or added from the new employee wizard and created in SAP afterwards. Distinct from app permission roles under Users & roles."
      />

      <PendingSapRolesCard pending={pending} />

      <DuplicateSapJobsCard conflicted={active.filter((r) => r.sap_conflicts.length > 0)} />

      <SapRoleSyncCard />

      <RoleCategoriesCard />

      <RolesImportCard />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, SAP job ID or resource"
            className="h-10 w-full rounded-token-sm border border-line-strong/20 bg-surface pl-9 pr-3 text-sm"
          />
        </div>
        {FILTERS.map((f) => {
          const on = filters.includes(f.key);
          return (
            <button
              key={f.key}
              type="button"
              aria-pressed={on}
              onClick={() =>
                setFilters((cur) =>
                  on ? cur.filter((k) => k !== f.key) : [...cur, f.key],
                )
              }
              className={
                'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ' +
                (on
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-line-strong/20 text-fg-muted hover:text-fg')
              }
            >
              {f.label}
            </button>
          );
        })}
        {search || filters.length > 0 ? (
          <span className="text-xs text-fg-dim">
            {visible.length} of {linked.length}
          </span>
        ) : null}
      </div>

      {list.isLoading ? (
        <SkeletonList rows={4} />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<Briefcase className="h-6 w-6" />}
          title={linked.length === 0 ? 'No SAP-linked roles yet' : 'No roles match'}
          description={
            linked.length === 0
              ? 'Run "Sync with SAP" above to pull the job list.'
              : 'Try clearing the search or filters.'
          }
        />
      ) : (
        <div className="space-y-2">
          {visible.map((r) =>
            editingId === r.id ? (
              <Card key={r.id}>
                <CardSection>
                  <RoleForm
                    key={r.id}
                    role={r}
                    categories={categoryList}
                    onDone={() => setEditingId(null)}
                  />
                </CardSection>
              </Card>
            ) : (
              <Card key={r.id}>
                <CardSection className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-fg">{r.name}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {r.category_name ? (
                        <Pill tone="accent" size="sm">{r.category_name}</Pill>
                      ) : null}
                      <Pill tone="info" size="sm">SAP {r.external_ref}</Pill>
                      {r.sap_missing ? (
                        <Pill tone="danger" size="sm">Removed from SAP</Pill>
                      ) : null}
                      {r.sap_conflicts.length > 0 ? (
                        <Pill tone="danger" size="sm">
                          Conflict: duplicate of SAP job{' '}
                          {r.sap_conflicts.map((c) => c.job_id).join(', ')}
                        </Pill>
                      ) : null}
                      {!r.sap_missing && r.sap_resources.length === 0 ? (
                        <Pill tone="warning" size="sm">No tariff</Pill>
                      ) : null}
                      {r.sap_resources.map((res) => (
                        <Pill key={res.id} tone="neutral" size="sm">
                          <span className="font-mono">{res.id}</span>
                        </Pill>
                      ))}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button variant="secondary" size="sm" iconLeft={<Pencil className="h-4 w-4" />} onClick={() => setEditingId(r.id)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" iconLeft={<Archive className="h-4 w-4" />} onClick={() => setConfirm({ id: r.id, name: r.name })}>
                      Archive
                    </Button>
                  </div>
                </CardSection>
              </Card>
            ),
          )}
        </div>
      )}

      {archived.length > 0 ? (
        <div>
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="text-sm font-medium text-fg-muted hover:text-fg"
          >
            {showArchived ? 'Hide' : 'Show'} archived ({archived.length})
          </button>
          {showArchived ? (
            <div className="mt-2 space-y-2">
              {archived.map((r) => (
                <Card key={r.id}>
                  <CardSection className="flex items-center justify-between gap-3">
                    <div className="min-w-0 truncate text-fg-muted line-through">{r.name}</div>
                    <Button variant="ghost" size="sm" iconLeft={<RotateCcw className="h-4 w-4" />} onClick={async () => {
                      try {
                        await restore.mutateAsync(r.id);
                        toast.success('Role restored');
                      } catch (err) {
                        toast.error(err instanceof ApiError ? err.message : 'Could not restore');
                      }
                    }}>
                      Restore
                    </Button>
                  </CardSection>
                </Card>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <ConfirmModal
        open={!!confirm}
        title="Archive role?"
        message={confirm ? `"${confirm.name}" will be hidden from the picker. People and templates already using it keep it; you can restore it later.` : ''}
        tone="warning"
        confirmLabel="Archive"
        onConfirm={async () => {
          if (!confirm) return;
          try {
            await archive.mutateAsync(confirm.id);
            toast.success('Role archived');
          } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not archive');
          }
          setConfirm(null);
        }}
        onClose={() => setConfirm(null)}
      />
    </main>
  );
}
